/**
 * MobileFilterDrawer — bottom-sheet filters for phone.
 *
 * Trigger renders a compact "Filters (n)" pill on mobile; the drawer
 * itself is a bottom Sheet (safe-area aware). On tablet/desktop the
 * trigger and drawer are hidden — callers keep their inline filter row
 * for those breakpoints.
 */
import React, { useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function MobileFilterDrawer({
  activeCount = 0,
  onClear,
  onApply,
  children,
  triggerClassName,
  triggerLabel = "Filters",
  testid = "mobile-filter-drawer",
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testid}-trigger`}
        className={cn(
          "lg:hidden inline-flex items-center gap-1.5 h-11 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-800 dark:text-slate-100",
          triggerClassName,
        )}
      >
        <SlidersHorizontal className="w-4 h-4" />
        {triggerLabel}
        {activeCount > 0 && (
          <span
            data-testid={`${testid}-count`}
            className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold tabular-nums"
          >
            {activeCount}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          data-testid={testid}
          className="p-0 max-h-[90dvh] rounded-t-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col"
        >
          <SheetHeader className="shrink-0 px-5 pt-5 pb-3 border-b border-slate-100 dark:border-slate-800 flex-row items-center justify-between space-y-0">
            <SheetTitle className="text-base">{triggerLabel}</SheetTitle>
            <button
              onClick={() => setOpen(false)}
              className="w-9 h-9 -mr-2 inline-flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Close"
              data-testid={`${testid}-close`}
            >
              <X className="w-4 h-4" />
            </button>
          </SheetHeader>

          <div
            className="min-h-0 flex-1 overflow-y-auto px-5 py-4"
            data-testid={`${testid}-body`}
          >
            {children}
          </div>

          <div
            className="shrink-0 border-t border-slate-100 dark:border-slate-800 px-5 py-3 flex items-center gap-2 bg-white dark:bg-slate-900"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          >
            {onClear && (
              <Button
                variant="outline"
                onClick={() => { onClear(); }}
                data-testid={`${testid}-clear`}
                className="h-11 flex-1"
              >
                Clear
              </Button>
            )}
            <Button
              onClick={() => { onApply?.(); setOpen(false); }}
              data-testid={`${testid}-apply`}
              className="h-11 flex-1 bg-slate-900 hover:bg-slate-800 text-white"
            >
              Apply
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
